
import { getAircraftTypes } from '@/services/aircraftService';
import { getCities } from '@/services/cityService';
import { getOrganizations } from '@/services/organizationService';

export const parseFlightCSV = async (csvText: string): Promise<any[]> => {
  // Fetch lookup data
  const [aircraftTypes, cities, organizations] = await Promise.all([
    getAircraftTypes(),
    getCities(),
    getOrganizations()
  ]);

  // Create lookup maps
  const aircraftMap = new Map();
  aircraftTypes.forEach(at => {
    aircraftMap.set(at.code, at.id);
    aircraftMap.set(at.name, at.id);
  });

  const cityMap = new Map();
  cities.forEach(city => {
    cityMap.set(city.code, city.id);
    cityMap.set(city.name, city.id);
  });

  const orgMap = new Map();
  organizations.forEach(org => {
    orgMap.set(org.code, org.id);
    orgMap.set(org.name, org.id);
  });

  const lines = csvText.trim().split('\n');
  const headers = lines[0].split(',').map(header => header.trim().toLowerCase());
  
  const expectedHeaders = [
    'flight_number',
    'organization_code',
    'departure_city_code',
    'arrival_city_code',
    'aircraft_type_code',
    'scheduled_departure',
    'scheduled_arrival',
    'status',
    'status_reason',
    'gate'
  ];

  // Check for missing headers with better error message
  const missingHeaders = expectedHeaders.filter(header => !headers.includes(header));
  if (missingHeaders.length > 0) {
    const actualHeaders = headers.join(', ');
    const expectedHeadersList = expectedHeaders.join(', ');
    throw new Error(`Missing required columns: ${missingHeaders.join(', ')}\n\nYour CSV headers: ${actualHeaders}\nExpected headers: ${expectedHeadersList}\n\nPlease download the template for the correct format.`);
  }

  const flights = [];
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map(value => value.trim().replace(/"/g, ''));
    if (values.length !== headers.length) {
      throw new Error(`Row ${i + 1}: Expected ${headers.length} columns, got ${values.length}`);
    }

    const flight: any = {};
    headers.forEach((header, index) => {
      let value = values[index];
      
      // Handle empty values
      if (value === '' || value === 'NULL') {
        if (['status_reason', 'gate'].includes(header)) {
          flight[header] = null;
        } else {
          throw new Error(`Row ${i + 1}: Required field '${header}' cannot be empty`);
        }
      } else {
        // Handle different field types
        if (header === 'organization_code') {
          const orgId = orgMap.get(value);
          if (!orgId) {
            throw new Error(`Row ${i + 1}: Organization code '${value}' not found`);
          }
          flight['organization_id'] = orgId;
        } else if (header === 'departure_city_code') {
          const cityId = cityMap.get(value);
          if (!cityId) {
            throw new Error(`Row ${i + 1}: Departure city code '${value}' not found`);
          }
          flight['departure_city_id'] = cityId;
        } else if (header === 'arrival_city_code') {
          const cityId = cityMap.get(value);
          if (!cityId) {
            throw new Error(`Row ${i + 1}: Arrival city code '${value}' not found`);
          }
          flight['arrival_city_id'] = cityId;
        } else if (header === 'aircraft_type_code') {
          const aircraftId = aircraftMap.get(value);
          if (!aircraftId) {
            throw new Error(`Row ${i + 1}: Aircraft type code '${value}' not found`);
          }
          flight['aircraft_type_id'] = aircraftId;
        } else if (header === 'scheduled_departure' || header === 'scheduled_arrival') {
          const date = new Date(value);
          if (isNaN(date.getTime())) {
            throw new Error(`Row ${i + 1}: Invalid date format for '${header}'. Use ISO format (YYYY-MM-DDTHH:mm:ss)`);
          }
          flight[header] = date;
        } else if (header === 'status') {
          // Validate status values
          const validStatuses = ['On Time', 'Delayed', 'Cancelled'];
          if (!validStatuses.includes(value)) {
            throw new Error(`Row ${i + 1}: Invalid status '${value}'. Must be one of: ${validStatuses.join(', ')}`);
          }
          flight[header] = value;
        } else {
          flight[header] = value;
        }
      }
    });

    flights.push(flight);
  }

  return flights;
};
